import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { getDisplayLabel, priorityLabels } from '../../utils/labelMappings';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { stripHtml } from '../../utils/sanitize';

interface Approval {
  id: string;
  caseId: string;
  approverId: string;
  state: 'pending' | 'approved' | 'rejected';
  comments?: string;
  approvedAt: string;
  Case: {
    id: string;
    number: string;
    shortDescription: string;
    priority: string;
    problemState?: string;
    assessment2Summary?: string;
    resolution?: string;
  };
  User?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

const MyApprovals: React.FC = () => {
  const queryClient = useQueryClient();
  const { formatDate } = useDateFormatter();
  const [selectedApproval, setSelectedApproval] = useState<Approval | null>(null);
  const [comments, setComments] = useState<string>('');
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseAction, setResponseAction] = useState<'approved' | 'rejected'>('approved');

  // Fetch pending approvals
  const { data: approvals, isLoading } = useQuery({
    queryKey: ['my-approvals'],
    queryFn: async () => {
      const response = await api.get('/approvals/my-pending');
      return response.data as Approval[];
    }
  });

  // Mutation to respond to approval
  const respondToApprovalMutation = useMutation({
    mutationFn: async ({ approvalId, status, comments }: { approvalId: string; status: 'approved' | 'rejected'; comments?: string }) => {
      const response = await api.patch(`/approvals/${approvalId}/respond`, {
        status,
        comments
      });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(`Approval ${data.approval.state} successfully`);
      queryClient.invalidateQueries({ queryKey: ['my-approvals'] });
      setShowResponseModal(false);
      setSelectedApproval(null);
      setComments('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to respond to approval');
    }
  });

  const handleOpenResponseModal = (approval: Approval, action: 'approved' | 'rejected') => {
    setSelectedApproval(approval);
    setResponseAction(action);
    setShowResponseModal(true);
  };

  const handleSubmitResponse = () => {
    if (!selectedApproval) return;

    if (responseAction === 'rejected' && !comments.trim()) {
      toast.error('Please provide comments when rejecting');
      return;
    }

    respondToApprovalMutation.mutate({
      approvalId: selectedApproval.id,
      status: responseAction,
      comments: comments.trim() || undefined
    });
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'critical_1':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high_2':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'moderate_3':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low_4':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Pending Approvals</h1>
          <p className="mt-2 text-sm text-gray-700">
            Review and respond to approval requests assigned to you
          </p>
        </div>
      </div>

      {!approvals || approvals.length === 0 ? (
        <div className="mt-8 text-center py-12 bg-white rounded-lg shadow">
          <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No pending approvals</h3>
          <p className="mt-1 text-sm text-gray-500">
            You don't have any approval requests at this time.
          </p>
        </div>
      ) : (
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Problem
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Description
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Priority
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Requested By
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Requested At
                      </th>
                      <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {approvals.map((approval) => (
                      <tr key={approval.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                          <Link
                            to={`/problems/${approval.Case.id}`}
                            className="font-medium text-blue-600 hover:text-blue-900"
                          >
                            {approval.Case.number}
                          </Link>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {stripHtml(approval.Case.shortDescription)}
                        </td>
                        <td className="px-3 py-4 text-sm">
                          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold border ${getPriorityColor(approval.Case.priority)}`}>
                            {getDisplayLabel(approval.Case.priority, priorityLabels)}
                          </span>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900">
                          {approval.User ? (
                            `${approval.User.firstName} ${approval.User.lastName}`
                          ) : (
                            'System'
                          )}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {formatDate(approval.approvedAt)}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <button
                            onClick={() => handleOpenResponseModal(approval, 'approved')}
                            className="inline-flex items-center gap-1.5 text-green-600 hover:text-green-900 mr-4"
                          >
                            <CheckCircleIcon className="h-5 w-5" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleOpenResponseModal(approval, 'rejected')}
                            className="inline-flex items-center gap-1.5 text-red-600 hover:text-red-900"
                          >
                            <XCircleIcon className="h-5 w-5" />
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Response Modal */}
      {showResponseModal && selectedApproval && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowResponseModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${responseAction === 'approved' ? 'bg-green-100' : 'bg-red-100'} sm:mx-0 sm:h-10 sm:w-10`}>
                  {responseAction === 'approved' ? (
                    <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                  ) : (
                    <XCircleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  )}
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    {responseAction === 'approved' ? 'Approve' : 'Reject'} Approval Request
                  </h3>
                  <div className="mt-4 space-y-4">
                    <div className="bg-gray-50 rounded-md p-4">
                      <div className="flex items-center mb-2">
                        <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">Problem: {selectedApproval.Case.number}</span>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{stripHtml(selectedApproval.Case.shortDescription)}</p>
                      {selectedApproval.Case.assessment2Summary && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs font-medium text-gray-700 mb-1">Assessment Summary:</p>
                          <p className="text-sm text-gray-600">{selectedApproval.Case.assessment2Summary}</p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label htmlFor="comments" className="block text-sm font-medium text-gray-700">
                        Comments {responseAction === 'rejected' && <span className="text-red-500">*</span>}
                      </label>
                      <textarea
                        id="comments"
                        rows={4}
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder={responseAction === 'approved' ? 'Add optional comments...' : 'Please explain why you are rejecting...'}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleSubmitResponse}
                  disabled={respondToApprovalMutation.isPending || (responseAction === 'rejected' && !comments.trim())}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                    responseAction === 'approved'
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  } disabled:bg-gray-400 disabled:cursor-not-allowed`}
                >
                  {respondToApprovalMutation.isPending ? 'Processing...' : responseAction === 'approved' ? 'Approve' : 'Reject'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowResponseModal(false);
                    setComments('');
                  }}
                  disabled={respondToApprovalMutation.isPending}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyApprovals;
